/**
 * LetterManager — client bridge to the living-Grandma backend.
 *
 * Every meaningful moment in the garden triggers a new letter. The
 * server generates fresh prose via Claude, narrates it via Shimmer,
 * and returns the audio as base64 mp3. We queue it into the mailbox
 * and chime softly to let the players know Grandma wrote them.
 */

const LETTER_ENDPOINT = (() => {
  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  // Dev from localhost still hits the public server — we only have one backend
  return 'https://farm-api.jacobterrell.dev/heirloom/letter';
})();

const LETTERS_LIST_ENDPOINT = (roomCode) =>
  `https://farm-api.jacobterrell.dev/heirloom/letters/${encodeURIComponent(roomCode)}`;

const FULL_LETTER_ENDPOINT = (letterId) =>
  `https://farm-api.jacobterrell.dev/heirloom/letter/${encodeURIComponent(letterId)}`;

class LetterManagerClass {
  constructor() {
    this.roomCode = null;
    this.letters = []; // [{ id, text, audioBase64?, createdAt, trigger, plantId }]
    this._listeners = { new_letter: [] };
    this._pendingRequest = false;
  }

  attach(roomCode) {
    this.roomCode = roomCode;
    // Load persisted letters for this room from localStorage so mailbox survives reload
    try {
      const raw = localStorage.getItem(`heirloom_letters_${roomCode}`);
      if (raw) this.letters = JSON.parse(raw);
    } catch (_) { this.letters = []; }
  }

  on(event, handler) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(handler);
  }

  _emit(event, payload) {
    (this._listeners[event] || []).forEach(h => {
      try { h(payload); } catch (_) {}
    });
  }

  _persist() {
    if (!this.roomCode) return;
    try {
      localStorage.setItem(`heirloom_letters_${this.roomCode}`, JSON.stringify(this.letters));
    } catch (_) {}
  }

  /** Called when WebSocket relay forwards a __letter broadcast from the server */
  receiveFromRelay(payload) {
    if (!payload || !payload.id) return;
    if (this.letters.find(l => l.id === payload.id)) return; // dedupe
    const letter = { ...payload, unread: true };
    this.letters.push(letter);
    this._persist();
    this._emit('new_letter', letter);
  }

  /** Ask the server to generate a fresh letter from Grandma. Only the initiator
   *  of a memory should call this — the server broadcasts the result to the
   *  whole room via the __letter relay, so peers receive it automatically. */
  async requestLetter(context = {}) {
    if (!this.roomCode) return null;
    if (this._pendingRequest) return null; // one at a time
    this._pendingRequest = true;
    // Always stamp the player's local time so Grandma writes in their timezone
    const enriched = { ...context, localHour: new Date().getHours() };
    try {
      const res = await fetch(LETTER_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomCode: this.roomCode, context: enriched }),
      });
      if (!res.ok) throw new Error(`letter fetch ${res.status}`);
      const { letter } = await res.json();
      if (!letter) return null;
      // Add to local list if not already there (server also broadcasts it
      // but we add locally for the initiator to guarantee the chime fires)
      if (!this.letters.find(l => l.id === letter.id)) {
        this.letters.push({ ...letter, unread: true });
        this._persist();
        this._emit('new_letter', letter);
      }
      return letter;
    } catch (e) {
      console.warn('[Letter] fetch failed:', e.message);
      return null;
    } finally {
      this._pendingRequest = false;
    }
  }

  getLetters() {
    // Newest first
    return [...this.letters].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }

  unreadCount() {
    return this.letters.filter(l => l.unread).length;
  }

  markRead(letterId) {
    const letter = this.letters.find(l => l.id === letterId);
    if (letter) { letter.unread = false; this._persist(); }
  }

  /** Fetch full letter (with audioBase64) if we only have a slim version */
  async fetchFullLetter(letterId) {
    const local = this.letters.find(l => l.id === letterId);
    if (local && local.audioBase64) return local;
    try {
      const res = await fetch(FULL_LETTER_ENDPOINT(letterId));
      if (!res.ok) return null;
      const { letter } = await res.json();
      // Merge audioBase64 back into local copy
      if (local && letter) { local.audioBase64 = letter.audioBase64; this._persist(); return local; }
      return letter;
    } catch (_) { return null; }
  }
}

export const LetterManager = new LetterManagerClass();
